import React, { useEffect, useState } from "react";
import { Card, Table, Button, Tag, Space, message, DatePicker, Tooltip } from "antd";
import styled from "styled-components";
import {
  DownloadOutlined,
  SearchOutlined,
  InboxOutlined,
  FilePdfFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import { useLojaSelecionada } from "../../../../queries/useLojaSelecionada";

const { RangePicker } = DatePicker;

const moneyBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

//const API_BASE = "http://localhost:8050";
const API_BASE = "https://apicontabil.techspdv.com.br";

const ExportacaoNfceTabela = () => {
  const accessToken = window.localStorage.getItem("accessToken");
  const { data: lojaSelecionada } = useLojaSelecionada();

  const lojaIdSafe =
    typeof lojaSelecionada === "string"
      ? lojaSelecionada.trim()
      : typeof lojaSelecionada === "object" && lojaSelecionada?.id
        ? String(lojaSelecionada.id).trim()
        : "";

  const temLojaSelecionada = lojaIdSafe.length > 0;

  const [periodo, setPeriodo] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ agora a seleção é um array de chaves
  const [selectedChaves, setSelectedChaves] = useState([]);



  const getHeaders = () => {
    if (!accessToken) return {};
    return {
      Authorization: `Bearer ${accessToken}`,
    };
  };

  const fetchNotas = async ({ page, size } = {}) => {
    if (!accessToken) {
      message.error("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!temLojaSelecionada) {
      setDataSource([]);
      setPagination((p) => ({ ...p, total: 0 }));
      setSelectedChaves([]);
      return;
    }

    if (!periodo || periodo.length !== 2 || !periodo[0] || !periodo[1]) {
      message.warning("Selecione o período.");
      return;
    }

    const uiPage = page ?? pagination.current;
    const pageSize = size ?? pagination.pageSize;

    const dtIni = dayjs(periodo[0]).format("YYYY-MM-DD");
    const dtFim = dayjs(periodo[1]).format("YYYY-MM-DD");

    try {
      setLoading(true);

      const resp = await axios.get(`${API_BASE}/integracao/notas`, {
        params: {
          lojaId: lojaIdSafe,
          tipoDocumento: "NFCE",
          dtIni,
          dtFim,
          page: uiPage - 1,
          size: pageSize,
        },
        headers: getHeaders(),
      });

      const pageData = resp.data || {};
      const content = Array.isArray(pageData.content) ? pageData.content : [];

      const rows = content.map((r) => ({
        id: String(r.id),
        chave: r.chaveDeConsulta,
        numero: r.numero,
        serie: r.serie,
        dataEmissao: r.dataEmissao,
        tipoDocumento: r.tipoDocumento,
        status: r.status,
        possuiCancelamento: Boolean(r.possuiCancelamento),
        valor: r.total,
      }));

      setDataSource(rows);

      setPagination((prev) => ({
        ...prev,
        current: (pageData.number ?? uiPage - 1) + 1,
        pageSize: pageData.size ?? pageSize,
        total: pageData.totalElements ?? rows.length,
      }));
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        message.error("Sessão expirada. Faça login novamente.");
      } else {
        message.error("Erro ao buscar notas.");
      }
      setDataSource([]);
      setPagination((p) => ({ ...p, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!temLojaSelecionada) {
      setDataSource([]);
      setPagination((p) => ({ ...p, current: 1, total: 0 }));
      setSelectedChaves([]);
      return;
    }

    setPagination((p) => ({ ...p, current: 1 }));
    setDataSource([]);
    setSelectedChaves([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchNotas({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temLojaSelecionada, lojaIdSafe]);

  const baixarXmlLinha = async (nota) => {
    if (!accessToken) {
      message.error("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!temLojaSelecionada) {
      message.warning("Selecione uma loja.");
      return;
    }

    if (!nota?.chave) {
      message.error("Chave da nota não encontrada.");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE}/integracao/notas/xml`, {
        params: {
          lojaId: lojaIdSafe,
          chave: nota.chave,
        },
        responseType: "blob",
        headers: getHeaders(),
      });

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const nome = nota.possuiCancelamento ? "XML-AUT+CANC" : "XML-AUT";
      link.download = `${nome}-${nota.chave}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      message.success("Download iniciado.");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        message.error("Sessão expirada. Faça login novamente.");
      } else if (err.response?.status === 404) {
        message.error("XML não encontrado para essa chave.");
      } else {
        message.error("Erro ao baixar XML.");
      }
    } finally {
      setLoading(false);
    }
  };

  const baixarPdfLinha = async (nota) => {
    if (!accessToken) {
      message.error("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!temLojaSelecionada) {
      message.warning("Selecione uma loja.");
      return;
    }

    const chave = String(nota?.chave || "").trim();
    if (!chave) {
      message.error("Chave da nota não encontrada.");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE}/integracao/notas/pdf`, {
        params: { lojaId: lojaIdSafe, chave },
        responseType: "blob",
        headers: getHeaders(),
        validateStatus: (s) => s >= 200 && s < 500,
      });

      if (response.status !== 200) {
        try {
          const text = await response.data.text();
          message.error(text || "Erro ao baixar PDF.");
        } catch {
          message.error("Erro ao baixar PDF.");
        }
        return;
      }

      const blob = response.data;
      if (!blob || blob.size === 0) {
        message.error("PDF vazio retornado pela API.");
        return;
      }

      let filename = `PDF-${chave}.pdf`;
      const cd = response.headers?.["content-disposition"];
      if (cd) {
        const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(cd);
        if (match?.[1]) {
          filename = decodeURIComponent(match[1].replace(/"/g, "").trim());
        }
      }

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      message.success("Download do PDF iniciado.");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        message.error("Sessão expirada. Faça login novamente.");
      } else if (err.response?.status === 404) {
        message.error("PDF não encontrado para essa chave.");
      } else {
        message.error("Erro ao baixar PDF.");
      }
    } finally {
      setLoading(false);
    }
  };
  const exportarTudo = async () => {
    try {
      const dtIni = periodo?.[0]?.format('YYYY-MM-DD');
      const dtFim = periodo?.[1]?.format('YYYY-MM-DD');

      if (!dtIni || !dtFim) {
        console.error('Período inválido');
        return;
      }

      const resp = await axios.post(
        `${API_BASE}/integracao/notas/xml/exportar?cnpjEmissor=${lojaSelecionada?.cnpj}&tipoDocumento=NFCE&dtIni=${dtIni}&dtFim=${dtFim}`,
        null,
        {
          headers: getHeaders(),
          responseType: 'blob',
        }
      );

      const disposition = resp.headers['content-disposition'];
      let fileName = `XML-PERIODO-${Date.now()}.zip`;

      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([resp.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar XML do período:', error);
    }
  };

  const gerarRelatorio = async () => {
  try {
    const dtIni = periodo?.[0]?.format('YYYY-MM-DD');
    const dtFim = periodo?.[1]?.format('YYYY-MM-DD');

    if (!dtIni || !dtFim) return;

    const resp = await axios.get(
      `${API_BASE}/integracao/notas/relatorio?cnpjEmissor=${lojaSelecionada?.cnpj}&tipoDocumento=NFCE&dtIni=${dtIni}&dtFim=${dtFim}`,
      {
        headers: getHeaders(),
        responseType: 'blob',
      }
    );

    const disposition = resp.headers['content-disposition'];
    let fileName = `RELATORIO-NOTAS-${Date.now()}.pdf`;

    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) {
        fileName = match[1];
      }
    }

    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erro ao gerar relatório:', error);
  }
};

  const baixarXmlLote = async () => {
    try {
      const resp = await axios.post(
        `${API_BASE}/integracao/notas/xml/lote?cnpjEmissor=${lojaSelecionada?.cnpj}`,
        selectedChaves,
        {
          headers: {
            ...getHeaders(),
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );

      const disposition = resp.headers['content-disposition'];
      let fileName = `XML-NOTAS-${Date.now()}.zip`;

      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([resp.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar ZIP:', error);
    }
  };

  const columns = [
    {
      title: "Data",
      dataIndex: "dataEmissao",
      key: "dataEmissao",
      width: 170,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
      sorter: (a, b) => new Date(a.dataEmissao) - new Date(b.dataEmissao),
    },
    {
      title: "Documento",
      dataIndex: "tipoDocumento",
      key: "tipoDocumento",
      width: 120,
      render: (v) => <Tag>{String(v || "").toUpperCase()}</Tag>,
    },
    {
      title: "Nº/Série",
      key: "numeroSerie",
      width: 120,
      render: (_, r) =>
        r.numero != null || r.serie != null
          ? `${r.numero ?? "-"} / ${r.serie ?? "-"}`
          : "-",
    },
    {
      title: "Chave",
      dataIndex: "chave",
      key: "chave",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (v) => (
        <Space>
          <Tag color={String(v || "").toUpperCase() === "CANCELADA" ? "red" : "green"}>
            {String(v || "").toUpperCase() || "—"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      width: 120,
      align: "right",
      render: (v) => (v != null ? moneyBRL(v) : "-"),
    },
    {
      title: "Ações",
      key: "acoes",
      width: 210,
      render: (_, nota) => (
        <Space>
          <Tooltip title="Baixar XML da nota">
            <BtnBlack
              shape="circle"
              icon={<DownloadOutlined />}
              onClick={() => baixarXmlLinha(nota)}
            />
          </Tooltip>

          <Tooltip title="Baixar PDF da nota">
            <BtnBlack
              shape="circle"
              icon={<FilePdfFilled />}
              onClick={() => baixarPdfLinha(nota)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedChaves,
    preserveSelectedRowKeys: true,

    onSelect: (record, selected) => {
      const chave = String(record.chave || "").trim();
      if (!chave) return;

      setSelectedChaves((prev) => {
        if (selected) {
          if (prev.includes(chave)) return prev;
          return [...prev, chave];
        }

        return prev.filter((item) => item !== chave);
      });
    },

    onSelectAll: (selected, selectedRows, changeRows) => {
      const chavesPagina = changeRows
        .map((row) => String(row.chave || "").trim())
        .filter(Boolean);

      setSelectedChaves((prev) => {
        if (selected) {
          const merged = new Set([...prev, ...chavesPagina]);
          return Array.from(merged);
        }

        return prev.filter((item) => !chavesPagina.includes(item));
      });
    },

    getCheckboxProps: () => ({
      disabled: !temLojaSelecionada,
    }),
  };

  const handleTableChange = (pag) => {
    fetchNotas({ page: pag.current, size: pag.pageSize });
  };

  return (
    <Page>
      <TopBar>
        <TitleArea>
          <h2 style={{ margin: 0 }}>Exportação de NFC-e</h2>
          <small style={{ color: "#777" }}>
            Busque as notas por período, selecione e baixe XML.
          </small>
        </TitleArea>

        <RightControls>
          <RangePicker
            value={periodo}
            onChange={(val) => setPeriodo(val || [dayjs().subtract(7, "day"), dayjs()])}
          />

          <BtnBlack
            icon={<SearchOutlined />}
            onClick={() => {
              setPagination((p) => ({ ...p, current: 1 }));
              fetchNotas({ page: 1 });
            }}
            disabled={!temLojaSelecionada}
          >
            Buscar
          </BtnBlack>

          <BtnBlack
            icon={<InboxOutlined />}
            onClick={baixarXmlLote}
            disabled={!temLojaSelecionada || !selectedChaves.length}
          >
            Exportar Selecionadas
          </BtnBlack>
          <BtnBlack
            icon={<InboxOutlined />}
            onClick={exportarTudo}
            disabled={!temLojaSelecionada}
          >
            Exportar Tudo
          </BtnBlack>
          <BtnBlack
            icon={<InboxOutlined />}
            onClick={gerarRelatorio}
            disabled={!temLojaSelecionada}
          >
            Gerar Relatório
          </BtnBlack>
        </RightControls>
      </TopBar>

      <CardStyled>
        <Table
          rowKey="chave"
          rowSelection={rowSelection}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          locale={{
            emptyText: temLojaSelecionada
              ? "Nenhuma nota encontrada no período."
              : "Selecione uma loja para buscar.",
          }}
        />
      </CardStyled>
    </Page>
  );
};

export default ExportacaoNfceTabela;

const Page = styled.div`
  padding: 20px;
  margin-top: 95px;
  margin-bottom: 80px;
`;

const TopBar = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const CardStyled = styled(Card)`
  border-radius: 14px !important;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
`;

const BtnBlack = styled(Button)`
  background: #000 !important;
  border-color: #000 !important;
  color: #fff !important;
  font-weight: 600;

  .anticon {
    color: #fff !important;
  }

  &:hover {
    background: #111 !important;
    border-color: #111 !important;
    color: #fff !important;
  }

  &:disabled {
    background: #ccc !important;
    border-color: #ccc !important;
    color: #666 !important;
  }
`;